import { useState, useEffect } from 'react'
import { getDataAPI, postDataAPI } from '../lib/api'
import type {
  SectionGranularConfig,
  ShowsGranularConfig,
  MusicGranularConfig,
  ScheduleGranularConfig,
  OverviewGranularConfig,
  FinanceGranularConfig,
  TasksGranularConfig,
  KnowledgeGranularConfig,
  AccessLevel,
} from '../types/permissions'
import { getDefaultSectionConfig } from '../types/permissions'

// ─── Types ───────────────────────────────────────────────────────────

type UserPermissions = {
  sections: string[]
  sectionConfig: SectionGranularConfig
}

// ─── Legacy app → section mapping (still used for backends that only
//     return an `apps` array without granular config) ─────────────────

const APP_TO_SECTIONS: Record<string, string[]> = {
  bookings:   ['shows', 'schedule'],
  tourmngr:   ['shows', 'schedule'],
  catalog:    ['music'],
  knowledge:  ['knowledge'],
  workflows:  ['schedule'],
}

/**
 * Recursively search an object for an `apps` array (string[]).
 * Returns the first match found, or null.
 */
function findAppsArray(obj: any, depth = 0): string[] | null {
  if (!obj || typeof obj !== 'object' || depth > 5) return null
  if (Array.isArray(obj.apps) && obj.apps.every((a: any) => typeof a === 'string')) {
    return obj.apps
  }
  for (const key of ['data', 'permissions', 'result', 'member', 'teamMember']) {
    if (obj[key] && typeof obj[key] === 'object') {
      const found = findAppsArray(obj[key], depth + 1)
      if (found) return found
    }
  }
  return null
}

/** Recursively search for a `sectionConfig` object from the API response */
function findSectionConfig(obj: any, depth = 0): SectionGranularConfig | null {
  if (!obj || typeof obj !== 'object' || depth > 5) return null
  if (obj.sectionConfig && typeof obj.sectionConfig === 'object') {
    return obj.sectionConfig as SectionGranularConfig
  }
  for (const key of ['data', 'permissions', 'result', 'member', 'teamMember']) {
    if (obj[key] && typeof obj[key] === 'object') {
      const found = findSectionConfig(obj[key], depth + 1)
      if (found) return found
    }
  }
  return null
}

/** Recursively search for a `sections` array from the API response */
function findSectionsArray(obj: any, depth = 0): string[] | null {
  if (!obj || typeof obj !== 'object' || depth > 5) return null
  if (Array.isArray(obj.sections) && obj.sections.every((s: any) => typeof s === 'string')) {
    return obj.sections
  }
  for (const key of ['data', 'permissions', 'result', 'member', 'teamMember']) {
    if (obj[key] && typeof obj[key] === 'object') {
      const found = findSectionsArray(obj[key], depth + 1)
      if (found) return found
    }
  }
  return null
}

/** Given the raw `apps` array, derive the set of allowed route sections. */
function deriveSections(apps: string[]): string[] {
  const sections = new Set<string>()
  sections.add('team-hub')
  sections.add('settings')
  for (const appId of apps) {
    const mapped = APP_TO_SECTIONS[appId]
    if (mapped) mapped.forEach((s) => sections.add(s))
  }
  return Array.from(sections)
}

// ─── Hook ────────────────────────────────────────────────────────────

export function useUserPermissions() {
  // ── Synchronously resolve the fast paths (artist / no-user / no-artist)
  //    so ProtectedRoute never flashes "Loading..." for these cases. ──
  const { initPerms, initLoading, initIsTeamMember, initSelectedArtist, initUserData } = (() => {
    try {
      const user = localStorage.getItem('dizeeUser')
      const token = localStorage.getItem('dizeeToken')
      const selectedArtist = localStorage.getItem('selectedArtist')
      if (!user || !token) return { initPerms: null, initLoading: false, initIsTeamMember: false, initSelectedArtist: null, initUserData: null }
      const userData = JSON.parse(user)
      const isTM = ['manager', 'support', 'promoter'].includes(userData.professionRole)
      // Artists get full access immediately — no async work needed
      if (!isTM) return { initPerms: null, initLoading: false, initIsTeamMember: false, initSelectedArtist: selectedArtist, initUserData: userData }
      // Team member without a selected artist
      if (!selectedArtist) return { initPerms: { sections: ['team-hub', 'settings'], sectionConfig: {} } as UserPermissions, initLoading: false, initIsTeamMember: true, initSelectedArtist: null, initUserData: userData }
      // Team member with selected artist — needs async fetch
      return { initPerms: null, initLoading: true, initIsTeamMember: true, initSelectedArtist: selectedArtist, initUserData: userData }
    } catch {
      return { initPerms: null, initLoading: false, initIsTeamMember: false, initSelectedArtist: null, initUserData: null }
    }
  })()

  const [permissions, setPermissions] = useState<UserPermissions | null>(initPerms)
  const [loading, setLoading] = useState(initLoading)

  useEffect(() => {
    // Fast paths were already handled synchronously above — skip async work
    if (!initIsTeamMember || !initSelectedArtist || !initUserData) return

    const fetchPermissions = async () => {
      try {
        const userData = initUserData!
        const artist = JSON.parse(initSelectedArtist!)
        const role = (userData.professionRole === 'support'
          ? 'support'
          : userData.professionRole === 'promoter'
            ? 'promoter'
            : 'manager') as 'manager' | 'support' | 'promoter'

        // --- Attempt 1: POST team/member-permissions ---
        let apps: string[] | null = null
        let sections: string[] | null = null
        let sectionConfig: SectionGranularConfig | null = null

        try {
          const res = await postDataAPI('team/member-permissions', {
            artistId: artist._id || artist.id,
            teamMemberEmail: userData.email,
          })
          console.log('[useUserPermissions] POST team/member-permissions response:', JSON.stringify(res))

          // Try to find the new granular format first
          sections = findSectionsArray(res)
          sectionConfig = findSectionConfig(res)

          // Fallback to legacy apps array
          if (!sections) {
            apps = findAppsArray(res)
          }
        } catch (err) {
          console.warn('[useUserPermissions] POST team/member-permissions failed:', err)
        }

        // --- Attempt 2 (fallback): GET team/member/:id ---
        if (!sections && !apps) {
          try {
            const memberId = artist.teamMemberId || artist.memberId
            if (memberId) {
              const res2 = await getDataAPI(`team/member/${memberId}`)
              console.log('[useUserPermissions] GET team/member fallback response:', JSON.stringify(res2))
              sections = findSectionsArray(res2)
              sectionConfig = findSectionConfig(res2)
              if (!sections) apps = findAppsArray(res2)
            }
          } catch (err2) {
            console.warn('[useUserPermissions] GET team/member fallback failed:', err2)
          }
        }

        console.log('[useUserPermissions] resolved — sections:', sections, 'apps:', apps, 'sectionConfig:', sectionConfig)

        // --- Fallback: check localStorage for granular config saved by the artist ---
        if (!sectionConfig) {
          try {
            const localKey = `dizee:granularPerms:${artist._id || artist.id}:${userData._id}`
            const localRaw = localStorage.getItem(localKey)
            if (localRaw) {
              sectionConfig = JSON.parse(localRaw) as SectionGranularConfig
              console.log('[useUserPermissions] loaded sectionConfig from localStorage fallback')
            }
          } catch { /* ignore */ }
        }

        // Build final permissions
        if (sections && sections.length > 0) {
          const finalSections = ['team-hub', 'settings', ...sections.filter(s => s !== 'team-hub' && s !== 'settings')]
          setPermissions({
            sections: finalSections,
            sectionConfig: sectionConfig || getDefaultSectionConfig(role),
          })
        } else if (apps && apps.length > 0) {
          setPermissions({
            sections: deriveSections(apps),
            sectionConfig: sectionConfig || getDefaultSectionConfig(role),
          })
        } else {
          console.warn('[useUserPermissions] Could not resolve permissions — granting role defaults')
          if (role === 'manager') {
            setPermissions({
              sections: ['team-hub', 'settings', 'shows', 'schedule', 'music', 'links', 'knowledge', 'team'],
              sectionConfig: getDefaultSectionConfig('manager'),
            })
          } else if (role === 'promoter') {
            setPermissions({
              sections: ['team-hub', 'settings', 'shows', 'team'],
              sectionConfig: getDefaultSectionConfig('promoter'),
            })
          } else {
            setPermissions({
              sections: ['team-hub', 'settings', 'shows', 'schedule'],
              sectionConfig: getDefaultSectionConfig('support'),
            })
          }
        }
        
        setLoading(false)
      } catch (error) {
        console.error('[useUserPermissions] Unexpected error:', error)
        setPermissions({ sections: ['team-hub', 'settings'], sectionConfig: {} })
        setLoading(false)
      }
    }

    fetchPermissions()
  }, [])

  // ─── Accessors ───────────────────────────────────────────────────

  /** Check if the user can visit a section at all */
  const hasPermission = (sectionId: string): boolean => {
    if (permissions === null) return true // artist = full access
    if (!permissions) return false
    return permissions.sections.includes(sectionId)
  }

  /** Get the granular config for a specific section (null = unrestricted / artist) */
  const getSectionConfig = <T = any>(sectionId: string): T | null => {
    if (permissions === null) return null // artist = no restrictions
    if (!permissions?.sectionConfig) return null
    return (permissions.sectionConfig as any)[sectionId] ?? null
  }

  /** Convenience: get Shows config */
  const getShowsConfig = (): ShowsGranularConfig | null => getSectionConfig<ShowsGranularConfig>('shows')

  /** Convenience: get Music config */
  const getMusicConfig = (): MusicGranularConfig | null => getSectionConfig<MusicGranularConfig>('music')

  /** Convenience: get Schedule config */
  const getScheduleConfig = (): ScheduleGranularConfig | null => getSectionConfig<ScheduleGranularConfig>('schedule')

  /** Convenience: get Overview config */
  const getOverviewConfig = (): OverviewGranularConfig | null => getSectionConfig<OverviewGranularConfig>('overview')

  /** Convenience: get Finance config */
  const getFinanceConfig = (): FinanceGranularConfig | null => getSectionConfig<FinanceGranularConfig>('finance')

  /** Convenience: get Tasks config */
  const getTasksConfig = (): TasksGranularConfig | null => getSectionConfig<TasksGranularConfig>('tasks')

  /** Convenience: get Knowledge config */
  const getKnowledgeConfig = (): KnowledgeGranularConfig | null => getSectionConfig<KnowledgeGranularConfig>('knowledge')

  /** Check if user is a team member (not an artist) */
  const isTeamMember = permissions !== null

  /** Check if a section is view-only for this user */
  const isViewOnly = (sectionId: string): boolean => {
    if (permissions === null) return false // artist = full access
    const config = getSectionConfig<{ accessLevel?: AccessLevel }>(sectionId)
    if (!config) return false
    return config.accessLevel === 'view'
  }

  /** Check if financial data is hidden for a section (explicit opt-in: hidden unless financialAccess === true) */
  const isFinancialHidden = (sectionId: string): boolean => {
    if (permissions === null) return false // artist = can see everything
    const config = getSectionConfig<{ financialAccess?: boolean }>(sectionId)
    if (!config) return true // no config = gate financials
    return config.financialAccess !== true
  }

  return {
    permissions,
    loading,
    isTeamMember,
    hasPermission,
    isViewOnly,
    isFinancialHidden,
    getSectionConfig,
    getShowsConfig,
    getMusicConfig,
    getScheduleConfig,
    getOverviewConfig,
    getFinanceConfig,
    getTasksConfig,
    getKnowledgeConfig,
  }
}
